import { Link, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useTabVisibility, type MobileTab } from '@/hooks/useTabVisibility';
import { useToast } from '@/hooks/use-toast';
import { CaptureFab } from './CaptureFab';

// 5-tab bottom navigation (P0-IA-02): [Home] [Library] (Capture FAB) [Hub] [Inbox], role-gated.
// Rendered only inside the native shell (see MobileShell). Safe-area aware; no horizontal scroll.
//
// Layout is a fixed 3-zone flex row: [left group flex-1] [fixed-width centre slot] [right group flex-1].
// The Capture FAB lives inside the always-present centre slot, so it is centred by ordinary flex flow
// WITHOUT any CSS transform on an ancestor. This matters because a transform on an ancestor would
// establish the containing block for the FAB sheet's position:fixed overlay (the sheet would then be
// sized/positioned relative to the tiny FAB slot instead of the viewport). The sheet is additionally
// portaled to document.body (see CaptureFab) for belt-and-braces.
//
// The 3-zone structure is ALWAYS rendered — while permissions load AND when Capture is role-gated off —
// so the centre slot, and therefore every tab's horizontal position, NEVER shifts. When Capture is not
// permitted we keep the reserved slot but render a dimmed, disabled FAB that emits a "not available for
// your role" toast on tap, rather than collapsing the layout (which would re-centre the other tabs and
// read as an error state).

// Dimmed, disabled Capture FAB shown when the user's role doesn't grant capture. Mirrors CaptureFab's
// geometry exactly (h-14 w-14 -mt-6 ring) so the reserved slot looks identical; only muted + a toast.
function DisabledCaptureFab() {
  const { toast } = useToast();
  return (
    <button
      type="button"
      aria-label="Capture (not available for your role)"
      aria-disabled="true"
      onClick={() =>
        toast({
          title: 'Capture isn’t available',
          description: 'Capturing research isn’t available for your role.',
        })
      }
      className="flex h-14 w-14 -mt-6 cursor-not-allowed items-center justify-center rounded-full bg-muted text-muted-foreground/60 shadow-lg shadow-black/5 ring-4 ring-background transition-transform active:scale-95"
    >
      <Plus className="h-7 w-7" />
    </button>
  );
}

export function BottomTabBar() {
  const { tabs, showCapture, loading } = useTabVisibility();
  const { pathname } = useLocation();
  const isActive = (to: string) => pathname === to || pathname.startsWith(to + '/');

  // Split the visible tabs into balanced left/right groups around the fixed centre slot so the
  // FAB stays horizontally centred regardless of tab count (4 → 2/2, 3 → 2/1, etc).
  const mid = Math.ceil(tabs.length / 2);
  const left = tabs.slice(0, mid);
  const right = tabs.slice(mid);

  const renderTab = (t: MobileTab) => {
    const active = isActive(t.to);
    return (
      <Link
        key={t.key}
        to={t.to}
        className={`flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-medium transition-colors ${
          active ? 'text-primary' : 'text-muted-foreground'
        }`}
        aria-current={active ? 'page' : undefined}
      >
        <t.icon className="h-5 w-5" />
        <span>{t.label}</span>
      </Link>
    );
  };

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card/95 backdrop-blur-sm pb-[env(safe-area-inset-bottom)]"
      aria-label="Primary"
    >
      {/* The 3-zone layout is ALWAYS rendered, so the reserved centre slot keeps every tab in a fixed
          position regardless of whether Capture is loading, permitted, or role-gated off. */}
      <div className="mx-auto flex h-16 max-w-md items-stretch px-1">
        {/* Left tab group (empty during loading). */}
        <div className="flex flex-1 items-stretch">{left.map(renderTab)}</div>
        {/* Always-present fixed-width centre slot — the FAB's stable home, no transform. While
            permissions load we keep the slot empty (we don't yet know if Capture is allowed); once
            resolved we render the real FAB when permitted, else a dimmed/disabled FAB + toast. */}
        <div className="flex w-16 shrink-0 items-center justify-center">
          {loading ? null : showCapture ? <CaptureFab /> : <DisabledCaptureFab />}
        </div>
        {/* Right tab group (empty during loading). */}
        <div className="flex flex-1 items-stretch">{right.map(renderTab)}</div>
      </div>
    </nav>
  );
}
